import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { 
  Download, FileType, FileSpreadsheet, Sheet, FileText,
  Printer, Share2, Image, Lock,
} from 'lucide-react';
import ShareModal from '../Modals/ShareModal';
import {
  downloadCSV, downloadText, downloadExcel,
  downloadMapPDF, downloadMapPNG, triggerPrint,
} from '../../utils/exportFormats';
import { trackExport, trackPrint, checkPrintQuota } from '../../services/api';
import { buildExportMeta } from './buildExportMeta';

// Fire a window event so live output counters in the Profile modal can
// update without threading new props through MapView → Header → Profile.
const notifyOutputTracked = (kind, result) => {
  if (!result || result.allowed === false) return;
  try {
    window.dispatchEvent(new CustomEvent('krime:output-tracked', {
      detail: { kind, ...result }
    }));
  } catch { /* noop */ }
};

const EXPORT_FORMATS = ['text', 'pdf', 'csv', 'excel', 'png'];
const PLANS_WITH_EXPORTS = ['captain', 'chief', 'enterprise'];

const MapExportActions = ({
  selectedLocation, popupOpen, onMenuStateChange, activeTab, hasPreviousData,
  locationName, exportContext, exportContextRef, userPlan, onRequestUpgrade,
}) => {
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showShareModal, setShowShareModal]     = useState(false);
  const [busy, setBusy] = useState(null);
  const map = useMap();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.download-menu') && !event.target.closest('.download-button-map')) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  useEffect(() => { if (popupOpen) setShowDownloadMenu(false); }, [popupOpen]);

  useEffect(() => {
    if (onMenuStateChange) onMenuStateChange(showDownloadMenu);
  }, [showDownloadMenu, onMenuStateChange]);

  const handleMouseEnter = () => map.doubleClickZoom.disable();
  const handleMouseLeave = () => map.doubleClickZoom.enable();

  const plan = userPlan || 'free';
  const canExport = PLANS_WITH_EXPORTS.includes(plan);

  const handleExport = async (format) => {
    if (EXPORT_FORMATS.includes(format) && !canExport) {
      onRequestUpgrade?.('export_limit');
      setShowDownloadMenu(false);
      return;
    }

    setShowDownloadMenu(false);
    setBusy(format);
    try {
      const ctx = exportContextRef?.current ?? exportContext;
      if (!ctx) return;

      const mapContainerEl = map.getContainer();

      if (EXPORT_FORMATS.includes(format)) {
        const result = await trackExport(
          buildExportMeta(ctx || exportContext, { format })
        ).catch(() => null);
        if (result && result.allowed === false) {
          onRequestUpgrade?.('export_limit');
          return;
        }
        notifyOutputTracked('export', result);

        if (format === 'pdf')   await downloadMapPDF(ctx, mapContainerEl);
        if (format === 'png')   await downloadMapPNG(ctx, mapContainerEl);
        if (format === 'csv')   await downloadCSV(ctx);
        if (format === 'text')  await downloadText(ctx);
        if (format === 'excel') await downloadExcel(ctx);
      }

      if (format === 'print') {
        const preflight = await checkPrintQuota().catch(() => null);
        if (preflight && preflight.allowed === false) {
          onRequestUpgrade?.('print_limit');
          return;
        }
        trackPrint(buildExportMeta(ctx || exportContext))
          .then((result) => notifyOutputTracked('print', result))
          .catch(() => {});
        await triggerPrint(ctx, { mapContainerEl });
      }
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setBusy(null);
    }
  };

  const lockIcon = <Lock size={13} className="export-lock-icon export-lock-icon-free" />;

  const ExportRow = ({ format, label, Icon }) => (
    <button
      type="button"
      className={`map-export-item export-dropdown-item${!canExport ? ' export-item-free-locked' : ''}`}
      onClick={() => handleExport(format)}
    >
      {canExport ? (
        <>
          <span className="export-format-icon"><Icon size={16} /></span>
          <span className="export-item-label">{label}</span>
        </>
      ) : (
        <>
          <span className="export-format-icon export-format-icon-muted"><Icon size={16} /></span>
          <span className="export-item-label">{label}</span>
          {lockIcon}
        </>
      )}
    </button>
  );

  return (
    <div className="leaflet-bottom leaflet-right map-export-actions">
      <div 
        className="leaflet-control export-actions-control"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="dropdown-container">
          <button 
            className="dropdown-button share-button-map"
            onClick={() => setShowShareModal(true)}
          >
            <Share2 size={20} />
          </button>
        </div>
        
        <div className="dropdown-container">
          <button 
            className={`dropdown-button download-button-map ${showDownloadMenu ? 'active' : ''} ${busy ? 'busy' : ''}`}
            onClick={() => setShowDownloadMenu(!showDownloadMenu)}
            disabled={!!busy}
            title={busy ? `Exporting ${busy}…` : 'Download'}
          >
            <Download size={20} />
          </button>
          {showDownloadMenu && (
            <div className="map-export-menu download-menu">
              <ExportRow format="text" label="Text" Icon={FileType} />
              <ExportRow format="csv" label="CSV" Icon={FileSpreadsheet} />
              <ExportRow format="excel" label="Excel" Icon={Sheet} />
              <ExportRow format="pdf" label="PDF" Icon={FileText} />
              <ExportRow format="png" label="PNG" Icon={Image} />
              <button type="button" className="map-export-item export-dropdown-item" onClick={() => handleExport('print')}>
                <span className="export-format-icon"><Printer size={16} /></span>
                <span className="export-item-label">Print</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={window.location.href}
        locationName={locationName}
      />
    </div>
  );
};

export default MapExportActions;
